import { useCallback, useEffect, useState } from "react";
import { Target, Plus, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { PageHeader, EmptyState, Spinner } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { api, formatErr, brl, fmtDate } from "../lib/api";

const METRICS = [{ value: "clicks", label: "Cliques nos links" }, { value: "commission", label: "Comissões (R$)" }, { value: "contents", label: "Conteúdos criados" }];
const FIELDS = [
  { name: "title", label: "Nome da meta", required: true, placeholder: "Ex.: 500 cliques em junho" },
  { name: "metric", label: "Métrica", type: "select", options: METRICS, default: "clicks" },
  { name: "target", label: "Objetivo", type: "number", required: true },
  { name: "start_date", label: "Início", type: "date", required: true, half: true },
  { name: "end_date", label: "Fim", type: "date", required: true, half: true },
];
const fmt = (m, v) => (m === "commission" ? brl(v) : v);

export default function Goals() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/goals-progress").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (b) => { try { await api.post("/goals", b); toast.success("Meta criada!"); load(); return true; } catch (e) { toast.error(formatErr(e)); return false; } };
  const remove = async (id) => { if (!window.confirm("Excluir meta?")) return; await api.delete(`/goals/${id}`); load(); };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader eyebrow="Objetivos" title="Metas" desc="O progresso é calculado automaticamente a partir dos seus dados reais."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setOpen(true)} data-testid="goal-add-button"><Plus className="h-4 w-4 mr-1" />Nova meta</Button>} />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState icon={Target} title="Sem metas" desc="Defina objetivos de cliques, comissões ou conteúdos." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((g) => (
            <div key={g.id} className="card-lux p-5 rise" data-testid={`goal-card-${g.id}`}>
              <div className="flex justify-between items-start">
                <div><div className="font-display text-lg font-bold">{g.title}</div><div className="text-[11px] text-zinc-500">{METRICS.find((m) => m.value === g.metric)?.label} • {fmtDate(g.start_date)} a {fmtDate(g.end_date)}</div></div>
                {g.progress >= 100 ? <Trophy className="h-5 w-5 text-amber-400" /> : <button onClick={() => remove(g.id)} className="text-zinc-600 hover:text-rose-400" data-testid={`goal-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="flex items-end justify-between mt-5 mb-2">
                <div className="font-display text-3xl font-extrabold gold-text" data-testid={`goal-progress-${g.id}`}>{g.progress}%</div>
                <div className="text-xs text-zinc-400">{fmt(g.metric, g.current)} / {fmt(g.metric, g.target)}</div>
              </div>
              <Progress value={g.progress} className="h-2.5 bg-zinc-800" />
            </div>
          ))}
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="Nova meta" fields={FIELDS} initial={{ metric: "clicks", start_date: today.slice(0, 8) + "01", end_date: today }} testid="goal-form" onSubmit={save} />
    </div>
  );
}
